(() => {
class CartRecommendations {
  constructor(container) {
    this.container = container;
    this.cartHandles = []; 
    this.handlesInCart = [];
    this.wasCartEmpty = false;
    this.recommendationMap = {};

    if (!this.container) return;

    this.init();
    this.bindFormSubmit();
    this.bindCartChangeListener();
  }

  async init() {
    try {
      document.querySelector('.drawer-recommendations')?.classList.add('loading');

      const cart = await this.fetchCart();
      if (!cart.items.length) {
        this.wasCartEmpty = true;
        document.querySelector('.drawer-recommendations')?.classList.remove('loading');
        return;
      }

      this.wasCartEmpty = false;
      this.cartHandles = cart.items.map(item => item.handle);
      this.handlesInCart = [...this.cartHandles];

      const recommendationPromises = cart.items.map(item =>
        this.fetchRecommendations(item.product_id).then(recommendations => ({
          item,
          recommendations: recommendations || []
        }))
      );

      const allResults = await Promise.all(recommendationPromises);
      const fetchProductTasks = [];

      for (const { item, recommendations } of allResults) {
        for (const product of recommendations) {
          if (this.handlesInCart.includes(product.handle)) continue;

          this.handlesInCart.push(product.handle);

          if (!this.recommendationMap[product.handle]) {
            this.recommendationMap[product.handle] = new Set();
          }
          this.recommendationMap[product.handle].add(item.handle);

          fetchProductTasks.push(
            this.fetchProduct(product.handle).then(fullProduct => {
              if (!fullProduct || !fullProduct.variants.length) return;
              fullProduct.recommendedBy = item.handle;
              this.renderProduct(fullProduct);
            })
          );
        }
      }

      await Promise.all(fetchProductTasks);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      document.querySelector('.drawer-recommendations')?.classList.remove('loading');
    }
  }

  bindFormSubmit() {
    document.addEventListener('submit', (e) => {
      if (e.target.matches('.recommendation-atc-form')) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });
  }

  async handleAddToCart(form) {
    const button = form.querySelector('button.product__submit__add');
    const loader = button.querySelector('.btn__loader');
    const text = button.querySelector('.btn__text');

    let variantId;
    const formSelect = form.querySelector('select.productVariantSlider');
    const formRadio = form.querySelector('input[data-single-option-selector]:checked');

    if (formRadio) {
      variantId = formRadio.value;
    } else if (formSelect) {
      variantId = formSelect.value;
    }

    if (!variantId) {
      console.error('No variant selected');
      return;
    }

    button.classList.add('is-loading');
    loader.classList.remove('hidden');
    text.classList.add('hidden');
    button.disabled = true;

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (window.cart && typeof window.cart.getCart === 'function') {
        window.cart.getCart();
      }

      const updatedCart = await this.fetchCart();
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: { cart: updatedCart }
      }));
    } catch (error) {
      console.error('Add to cart failed', error);
    } finally {
      button.classList.remove('is-loading');
      loader.classList.add('hidden');
      text.classList.remove('hidden');
      button.disabled = false;
    }
  }

  async fetchCart() {
    const res = await fetch('/cart.js');
    return await res.json();
  }

  async fetchRecommendations(productId) {
    const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=16`);
    const data = await res.json();
    return data.products;
  }

  async fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    return await res.json();
  }

  bindVariantSync(container) {
    const radioInputs = container.querySelectorAll('input[data-single-option-selector]');
    const selectInput = container.querySelector('select.productVariantSlider');

    if (!radioInputs.length || !selectInput) return;

    // Radio to select
    radioInputs.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          selectInput.value = radio.value;
        }
      });
    });

    // Select to radio
    selectInput.addEventListener('change', () => {
      const selectedValue = selectInput.value;
      const matchingRadio = container.querySelector(`input[data-single-option-selector][value="${selectedValue}"]`);
      if (matchingRadio) {
        matchingRadio.checked = true;
        matchingRadio.dispatchEvent(new Event('change'));
      }
    });
  }

  renderProduct(product) {
    const getFormattedPrice = (priceInCents) => {
      const currency = Shopify?.currency?.active || 'PHP';
      const locale = currency === 'PHP' ? 'en-PH' : 'en-US';
      try {
        return (priceInCents / 100).toLocaleString(locale, {
          style: 'currency',
          currency: currency,
        });
      } catch (e) {
        return `${currency} ${(priceInCents / 100).toFixed(2)}`;
      }
    };

    const variants = product.variants || [];
    const firstVariant = variants[0];
    const optionName = typeof product.options[0] === 'object' ? product.options[0].name : product.options[0];
    const selectedVariantId = product.variants.find(v => v.available)?.id || product.variants[0]?.id;

    const ratingCount = product?.metafields?.reviews?.rating_count;
    const ratingValue = product?.metafields?.reviews?.rating?.value;
    const showRating = ratingCount && parseInt(ratingCount) > 0;

    const ratingHTML = `
      <div class="cart_ratting" style="display: none;">
        <div class="fera-wrapper ${showRating ? 'product-rating-wrapper' : 'show-all-rating-wrapper'}">
          <div style="display: none;"
            data-fera-widget="product_detail_rating"
            data-fera-id="fwid_2hNv2VM"
            data-rating-count="${ratingCount || ''}"
            data-rating-average="${ratingValue || ''}"
            data-product-id="${product.id}">
          </div>
          <div class="fera-content">
            ${showRating ? `
              <a class="fera-link" href="#reviews">${parseFloat(ratingValue).toFixed(1)}</a>` : `
              <a class="fera-link show-all-rating" href="#reviews">
                <div class="rating-wrapper">
                  <div data-fera-widget="overall_rating_banner" data-fera-id="fwid_37ytZF4"></div>
                </div>
              </a>`}
          </div>
        </div>
      </div>
    `;

    const variantOptions = variants.map(v => `
      <span class="radio__button">
        <input type="radio"
               data-single-option-selector
               data-index="option${v.position}"
               name="options[${optionName}]"
               value="${v.id}"
               id="product-${v.id}-${optionName}-${v.title}"
               ${v.id === selectedVariantId ? 'checked' : ''}
               ${v.available ? '' : 'disabled'}>
        <label for="product-${v.id}-${optionName}-${v.title}" class="${v.available ? '' : 'soldout'}">
          <span>${v.title}</span>
        </label>
      </span>
    `).join('');

    const variantOptionSelects = variants.map(v => `
      <option value="${v.id}" data-name="${v.title}" data-id="${v.id}"
         ${v.available ? '' : 'disabled class="soldOut" style="pointer-events: none; opacity: 0.7;color: #ddd;"'}
       > ${v.title} </option>
    `).join('');

    const productHTML = `
      <div class="collection_product"
           data-name="${product.title}"
           data-recommended-by="${product.recommendedBy || ''}"
           data-product-handle="${product.handle}">
        <div class="card-wrapper product-card-wrapper" style="--ratio-percent: 100%;">
          <div class="card__inner ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <a href="${product.url}" class="drawer-recommendation-card" style="display: none;"></a>
              <div class="media media--transparent media--hover-effect">
                <img src="${product.images?.[0] || ''}" alt="${product.title}" class="motion-reduce" loading="lazy"/>
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="clcard__information">
              <div class="card__information_row">
                ${ratingHTML}
                <h3 class="card__heading h5" id="title--${product.id}">
                  <a href="${product.url}" class="full-unstyled-link">${product.title}</a>
                </h3>
                <div class="card_product_price">
                  <p>${getFormattedPrice(firstVariant.price)}</p>
                </div>
              </div>
              <div class="card_product_add">
                <div class="product__form__outer">
                  <form class="recommendation-atc-form product-form">
                    <div class="product__selectors">
                      <div class="selector-wrapper selector-wrapper--fullwidth">
                        <fieldset class="radio__fieldset">
                          <div class="swatch-container">
                            <div class="swatch-content">${variantOptions}</div>
                          </div>
                        </fieldset>
                      </div>
                      <div class="productVariant_slider" style="display: none;">
                        <div class="productVariant_option_slider">
                          <p> ${optionName} </p>
                          <select class="productVariantSlider" name="selectBox">
                            ${variantOptionSelects}
                          </select>
                        </div>
                      </div>
                      <div class="product__submit">
                        <div class="product__submit__buttons">
                          <div class="product__submit__item">
                            <button type="submit" name="add" class="btn product__submit__add">
                              <span class="btn__text">Add to Cart</span>
                              <span class="btn__added">&nbsp;</span>
                              <span class="btn__loader hidden">
                                <svg height="18" width="18" class="svg-loader">
                                  <circle r="7" cx="9" cy="9"></circle>
                                  <circle stroke-dasharray="87.96 87.96" r="7" cx="9" cy="9"></circle>
                                </svg>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', productHTML);

    // 🔄 Bind variant sync logic for new product
    const productElement = this.container.querySelector(`[data-product-handle="${product.handle}"]:last-child`);
    this.bindVariantSync(productElement);
  }

  bindCartChangeListener() {
    document.addEventListener('theme:cart:change', async (e) => {
      const updatedCart = e.detail?.cart;
      if (!updatedCart) return;

      this.cartHandles = updatedCart.items.map(item => item.handle);
      const removedHandles = this.handlesInCart.filter(handle => !this.cartHandles.includes(handle));

      for (const [recommendedHandle, sourceHandles] of Object.entries(this.recommendationMap)) {
        removedHandles.forEach(removedHandle => {
          sourceHandles.delete(removedHandle);

          if (sourceHandles.size === 0) {
            const nodes = this.container.querySelectorAll(`[data-product-handle="${recommendedHandle}"]`);
            nodes.forEach(el => {
              el.style.transition = 'opacity 300ms ease';
              el.style.opacity = '0';
              setTimeout(() => el.remove(), 300);
            });

            this.handlesInCart = this.handlesInCart.filter(h => h !== recommendedHandle);
            delete this.recommendationMap[recommendedHandle];
          }
        });
      }

      for (const item of updatedCart.items) {
        const alreadyLoaded = Object.values(this.recommendationMap).some(set => set.has(item.handle));
        if (!alreadyLoaded) {
          await this.loadRecommendationsForProduct(item);
        }
        if (!this.handlesInCart.includes(item.handle)) {
          this.handlesInCart.push(item.handle);
        }
      }
    });
  }

  async loadRecommendationsForProduct(cartItem) {
    const productId = cartItem.product_id;
    const productHandle = cartItem.handle;

    try {
      const recommendations = await this.fetchRecommendations(productId);
      if (!recommendations?.length) return;

      const productsToFetch = recommendations.filter(
        product => !this.handlesInCart.includes(product.handle)
      );

      this.handlesInCart.push(...productsToFetch.map(p => p.handle));

      const fetchPromises = productsToFetch.map(p =>
        this.fetchProduct(p.handle).then(fullProduct => {
          if (!fullProduct?.variants?.length) return;
          fullProduct.recommendedBy = cartItem.handle;

          if (!this.recommendationMap[p.handle]) {
            this.recommendationMap[p.handle] = new Set();
          }
          this.recommendationMap[p.handle].add(cartItem.handle);

          this.renderProduct(fullProduct);
        }).catch(() => null)
      );

      await Promise.all(fetchPromises);
    } catch (err) {
      console.error('Error loading recommendations for cart item:', err);
    }
  }
}

// ✅ Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.drawer-recommendations .product-display').forEach(container => {
    new CartRecommendations(container);
  });
});
})();
